import * as React from 'react';
import {FormEvent} from 'react';
import {observer, useLocalObservable} from "mobx-react-lite";
import {Accordion, Alert, Button, Col, Form, Row} from "react-bootstrap";
import Backdrop from "../components/Backdrop";
import {requests} from "../api";
import {Link, Redirect, useHistory} from "react-router-dom";
import {IocStore} from "../stores/ioc";
import LogsStore from "../stores/logs";
import {runInAction} from "mobx";
import Recaptcha from "react-recaptcha";

let recaptchaInstance: any = null;

const AuthPage = observer(function AuthPage(props: { iocStore: IocStore, logsStore: LogsStore, hsId: string }) {
    const history = useHistory<{ check: string }>()
    if (!history.location.state) history.replace('/')

    const checkIoc = history.location.state && history.location.state.check === 'ioc'
    const checkLog = history.location.state && history.location.state.check === 'log'

    if (checkIoc && !props.iocStore.ioc) {
        history.replace('/')
    }
    if (checkLog && !props.logsStore.file) {
        history.replace('/')
    }

    const checkIocLoadable = checkIoc && props.iocStore.ioc
    const checkLogLoadable = checkLog && props.logsStore.file
    const loadable = (props.iocStore.code || props.logsStore.code) && (checkIocLoadable || checkLogLoadable)

    const localState = useLocalObservable(() => ({
        validatedEmail: false,
        validatedCode: false,
        emailSent: false,
        loading: false,
        errorEmail: '',
        errorCode: '',
        limitReached: false,
        lookupInterval: false,
        email: '',
        captcha: 'none',
        hubspotId: props.hsId,
        redirect: '',
        code: props.iocStore.code || props.logsStore.code,
        isBlogOptInChecked: true
    }))

    const processError = (err: Error) => {
        let errMsg = err.message
        if (errMsg === 'Forbidden') {
            errMsg = 'Invalid auth code'
        }
        runInAction(() => {
            localState.errorCode = errMsg
            if (errMsg === 'Daily requests limit reached, try again tomorrow.') {
                localState.limitReached = true
            }
        })
    }

    const loadIoc = () => runInAction(() => {
        localState.loading = true
        props.iocStore.setCode(localState.code)
        props.iocStore.loadData()
            .then(() => localState.redirect = '/ioc/' + props.iocStore.ioc)
            .catch(processError)
            .finally(() => runInAction(() => localState.loading = false))
    })

    const loadLogs = () => runInAction(() => {
        props.logsStore.setCode(localState.code)
        localState.loading = true
        props.logsStore.loadData()
            .then(() => localState.redirect = '/logs')
            .catch(processError)
            .finally(() => runInAction(() => localState.loading = false))
    })

    if (!localState.loading && !localState.errorCode && loadable) {
        if (checkIoc) {
            loadIoc()
        } else if (checkLog) {
            loadLogs()
        }
    }

    const handleEmail = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        localState.validatedEmail = true
        const isValid = e.currentTarget.checkValidity()
        if (!isValid) return false

        localState.loading = true
        localState.errorEmail = ""

        requests.get('/user/generate_token/' + localState.email + "/" + localState.captcha + "/" + localState.hubspotId + "/" + localState.isBlogOptInChecked)
            .then(() => {
                localState.loading = false
                localState.emailSent = true
            })
            .catch((err) => {
                localState.errorEmail = err.message === "Forbidden" ? "Invalid auth code" : err.message
                recaptchaInstance.reset()
            })
            .finally(() => {
                localState.loading = false
            })
    }

    const handleCode = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        localState.validatedCode = true
        const isValid = e.currentTarget.checkValidity()
        if (!isValid) return false

        if (checkIoc) {
            loadIoc()
        } else if (checkLog) {
            loadLogs()
        }
    }

    const checkCode = async () => {
        let response;
        try {
            response = await requests.get('/user/check_code')
        } catch (e) {return}

        const code = response.code;
        if (!code) return

        if (localState.lookupInterval) {
            // @ts-ignore
            clearInterval(localState.lookupInterval)
        }

        runInAction(() => localState.code = code)

        if (checkIoc) {
            loadIoc()
        } else if (checkLog) {
            loadLogs()
        }
    }

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        localState.isBlogOptInChecked = e.target.checked;
    };

    if (localState.redirect) {
        return <Redirect to={{pathname: localState.redirect}}/>
    }

    if (localState.emailSent && !localState.lookupInterval) {
        runInAction(() => {
            // @ts-ignore
            localState.lookupInterval = setInterval(checkCode, 3000)
        })
    }

    if (localState.errorCode && localState.errorCode === 'Invalid auth token') {
        props.iocStore.setCode('')
        props.logsStore.setCode('')
    }

    return <>
        {loadable && <>
            {localState.limitReached && <>
                <p>You have reached the maximum daily limit.</p>
                <p>You can submit additional IOCs tomorrow (the limit resets at midnight UTC) or <a href="https://www.threatstop.com/check-ioc-subscription" target="_blank" rel="noreferrer">sign-up</a> for a free 30-day trial to try the full version of CheckIOC and ThreatSTOP's security platform.</p>
            </>}
            {!localState.limitReached && <>
                <Backdrop hidden={!localState.loading}>
                    <p>Loading...</p>
                </Backdrop>
                {localState.errorCode ? <Alert variant="danger">
                    {localState.errorCode === 'Invalid auth token' && <p>We couldn't find this access token. Click <Link to="/">here</Link> to request a new one.</p>}
                    {localState.errorCode !== 'Invalid auth token' && <p>{localState.errorCode}</p>}
                </Alert> : ''}
            </>}
        </>}
        {!loadable && <>
            <Row>
                <Col>
                    {localState.errorEmail ? <Alert variant="danger">{localState.errorEmail}</Alert> : ''}
                    {localState.emailSent ?
                        <p>We have sent you an email with a confirmation link. Click the link to access your results.</p> :
                        <Form noValidate validated={localState.validatedEmail} onSubmit={handleEmail}>
                            <Backdrop hidden={!localState.loading}/>
                            <Form.Row>
                                <Form.Group as={Col}>
                                    <p>Please enter your email address to access the Check IOC feature and we'll email you an access code.</p>
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        onChange={(e) => localState.email = e.target.value}
                                        type="email"
                                        placeholder="jdoe@example.com"
                                        pattern="[^\s@]+@[^\s@]+\.[^\s@]{2,}"
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        Invalid email
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Form.Row>
                            <Form.Row>
                                <Form.Group>
                                    <Form.Check
                                        type="checkbox"
                                        label="Subscribe to blog updates"
                                        id="blogOptInCheckbox"
                                        checked={localState.isBlogOptInChecked}
                                        onChange={handleCheckboxChange}
                                    />
                                </Form.Group>
                            </Form.Row>
                            <Form.Row>
                                <Form.Group as={Col}>
                                    <Recaptcha
                                        sitekey="6LfOpZgaAAAAAN0NnxBskW-wk1G-domXzj3QJVi2"
                                        verifyCallback={(r) => localState.captcha = r}
                                        ref={e => recaptchaInstance = e}
                                    />
                                </Form.Group>
                            </Form.Row>
                            <Form.Row>
                                <Form.Group as={Col}>
                                    <Button type="submit">Get Code</Button>
                                </Form.Group>
                            </Form.Row>
                        </Form>
                    }
                </Col>
            </Row>
            <Row>
                <Col>
                    <Accordion defaultActiveKey={localState.errorCode ? "0" : ""}>
                        <Accordion.Toggle as="a" className="code-dropdown-handle" eventKey="0">
                            If you already have an access code, click here
                        </Accordion.Toggle>
                        <Accordion.Collapse eventKey="0">
                            <Form noValidate validated={localState.validatedCode} onSubmit={handleCode}>
                                <Backdrop hidden={!localState.loading}/>
                                {localState.errorCode ? <Alert variant="danger">{localState.errorCode}</Alert> : ''}
                                <Form.Row>
                                    <Form.Group as={Col}>
                                        <Form.Label>Code</Form.Label>
                                        <Form.Control
                                            onChange={(e) => localState.code = e.target.value}
                                            pattern="[a-zA-Z0-9]{6}"
                                            defaultValue={localState.code}
                                            placeholder="ABC123"
                                            required
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            Invalid Code
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Form.Row>
                                <Form.Row>
                                    <Form.Group as={Col}>
                                        <Button type="submit">Continue</Button>
                                    </Form.Group>
                                </Form.Row>
                            </Form>
                        </Accordion.Collapse>
                    </Accordion>
                </Col>
            </Row>
        </>}
    </>
})

export default AuthPage